import { PopupType } from '../../../components/popup/types';
import { Logger } from '../../../logger/Logger';
import { AcceptedBet } from '../../../store';
import type { CoreStoreType } from '../../../store/CoreStore';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import { betActions } from '../../../store/slices/bets/slice';
import { popupActions } from '../../../store/slices/popup/slice';
import { crossOriginCommunicationController } from '../../crossOriginCommunication/CrossOriginCommunicationControllerConnector';
import { OutgoingMessage } from '../../crossOriginCommunication/types';
import type { GameCommunicationErrorMessageType, IGameCommunicationProcessor } from '../types';

import { BaseGameCommunicationBetProcessor } from './BaseGameCommunicationBetProcessor';

export enum BetValidationErrors {
  // Special cases. Other than that, show generic error popup.
  BetNotAllowed = 20602,
  BetNotOnTime = 1007,
  DidntMeetMaximumTableLimit = 1012,
  DidntMeetMinimumTableLimit = 1013,
  ExternalIgnoreError = 20530,
  ExternalRetryError = 20621,
  GameIdMismatch = 1014,
  GameNotFound = 20038,
  InsufficientBalance = 20601,
  InternalServerError = 20003,
  InvalidHashCode = 20999,
  InvalidToken = 20510,
  NoResponse = 1047,
  OperatorInternalError = 101020,
  PlayerLimitReached = 20610,
  PlayerNotFound = 20511,
  RealityCheck = 20520,
  RequestTimeout = 1050,
  UnknownError = 0,
}

export interface BetValidationErrorMessage extends GameCommunicationErrorMessageType {
  betCode: string;
  code: string;
  desc: string;
}

export class GameCommunicationBetValidationErrorProcessor
  extends BaseGameCommunicationBetProcessor
  implements IGameCommunicationProcessor
{
  delay = 0;
  messageName = 'betValidationError';

  process(message: BetValidationErrorMessage, store: CoreStoreType): void {
    const state = store.getState();
    const betCode = Number.parseInt(message.betCode);
    const errorCode = Number.parseInt(message.code);
    const { acceptedBets } = state.bets;
    const updatedBets: AcceptedBet[] = [];

    for (let i = 0; i < acceptedBets.length; i++) {
      if (acceptedBets[i].betcode === betCode) {
        const newBet = Object.assign({}, acceptedBets[i]);
        newBet.errorCode = errorCode;
        updatedBets[i] = newBet;
        store.dispatch(betActions.wipeBet(betCode));
      } else {
        updatedBets[i] = acceptedBets[i];
      }
    }

    const turnOffAutoplay = (errorCode: number): void => {
      if (state.autoplay.isInProgress) {
        store.dispatch(autoplayActions.stop());
        Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, `Error: ${errorCode}`, false);
      }
    };

    switch (errorCode) {
      case BetValidationErrors.BetNotAllowed:
        store.dispatch(popupActions.pushPopup(PopupType.BetNotAllowed));
        break;
      case BetValidationErrors.BetNotOnTime:
        store.dispatch(popupActions.pushPopup(PopupType.BetNotOnTime));
        break;
      case BetValidationErrors.InsufficientBalance:
        store.dispatch(popupActions.pushPopup(PopupType.InsufficientFunds));
        break;
      case BetValidationErrors.PlayerLimitReached:
        store.dispatch(popupActions.pushPopup(PopupType.PlayerLimit));
        break;
      case BetValidationErrors.RealityCheck:
      case BetValidationErrors.DidntMeetMinimumTableLimit:
        break;
      case BetValidationErrors.DidntMeetMaximumTableLimit:
      case BetValidationErrors.ExternalIgnoreError:
      case BetValidationErrors.ExternalRetryError:
      case BetValidationErrors.GameIdMismatch:
      case BetValidationErrors.GameNotFound:
      case BetValidationErrors.InternalServerError:
      case BetValidationErrors.InvalidHashCode:
      case BetValidationErrors.InvalidToken:
      case BetValidationErrors.NoResponse:
      case BetValidationErrors.OperatorInternalError:
      case BetValidationErrors.PlayerNotFound:
      case BetValidationErrors.RequestTimeout:
      case BetValidationErrors.UnknownError:
      default:
        store.dispatch(popupActions.pushPopup(PopupType.GenericError));
        break;
    }
    turnOffAutoplay(errorCode);

    store.dispatch(betActions.setAcceptedBets(updatedBets));

    this.updateGameStatus(store);

    crossOriginCommunicationController.dispatch(OutgoingMessage.ErrorMessage, {
      errorCode: message.optExtErrorCode,
      errorMessage: message.optExtErrorMsg,
    });

    Logger.logFailure(
      'errors',
      'gameCommunicationError',
      'GameCommunicationBetValidationErrorProcessor',
      Date.now(),
      1,
      `${errorCode}`,
      false
    );
  }
}
